import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";

import { registeredFeatures } from "../../features";
import { RouteConstants } from "../../models/enums/route-constants.enum";
import { AuthenticateApp } from "../Core/AuthenticateApp";
import { RouteErrorBoundary } from "../Core/ErrorBoundary/RouteErrorBoundary";
import { LoadingRoute } from "../Pages/FullScreenInformation/LoadingRoute";
import { lazyRoute, lazyRouteWithGuards } from "./Functions/LazyRouteFunction";

const TermsOfUseGuard = lazy(() =>
  import("./Guards/TermsOfUseGuard").then(module => ({
    default: module.TermsOfUseGuard,
  })),
);

const BudgetGuard = lazy(() =>
  import("./Guards/BudgetGuard").then(module => ({
    default: module.BudgetGuard,
  })),
);

const AdminAreaGuard = lazy(() =>
  import("./Guards/AdminAreaGuard").then(module => ({
    default: module.AdminAreaGuard,
  })),
);

const UserSettingsAreaGuard = lazy(() =>
  import("./Guards/UserSettingsAreaGuard").then(module => ({
    default: module.UserSettingsAreaGuard,
  })),
);

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AuthenticateApp />,
      errorElement: <RouteErrorBoundary />,
      hydrateFallbackElement: <LoadingRoute />,
      children: [
        {
          element: <Navigate to={`/${RouteConstants.LandingPage}`} />,
          index: true,
        },
        {
          path: `/${RouteConstants.Logout}`,
          lazy: lazyRoute({
            importer: () => import("../Pages/Logout/Logout"),
            componentName: "PageLogout",
          }),
        },
        {
          path: `/${RouteConstants.RejectedTermsOfUse}`,
          lazy: lazyRouteWithGuards({
            importer: () => import("../Pages/TermsOfUseRejectedPage/TermsOfUseRejectedPage"),
            componentName: "TermsOfUseRejectedPage",
          }),
        },
        {
          path: `/${RouteConstants.LandingPage}`,
          lazy: lazyRouteWithGuards({
            importer: () => import("../Pages/Landing/LandingPage"),
            componentName: "LandingPage",
            guards: [TermsOfUseGuard],
          }),
        },
        {
          path: RouteConstants.NoBudget,
          lazy: lazyRoute({
            importer: () => import("../Pages/GlobalErrorsPage/GlobalErrorsPage"),
            componentName: "GlobalErrorsPage",
          }),
        },
        {
          path: `/${RouteConstants.AdminArea}`,
          lazy: lazyRouteWithGuards({
            importer: () => import("../Pages/AdminArea/AdminPage"),
            componentName: "AdminPage",
            guards: [AdminAreaGuard],
          }),
        },
        {
          path: `/${RouteConstants.UserSettingsArea}`,
          lazy: lazyRouteWithGuards({
            importer: () => import("../Pages/UserSettingsArea/UserSettingsPage"),
            componentName: "UserSettingsPage",
            guards: [UserSettingsAreaGuard],
          }),
        },
        {
          path: `/${RouteConstants.Ai}`,
          lazy: lazyRouteWithGuards({
            importer: () => import("../Pages/Feature/FeaturePage"),
            componentName: "FeaturePage",
            guards: [TermsOfUseGuard, BudgetGuard],
          }),
          children: registeredFeatures.map(f => f.route),
        },
        {
          path: "*",
          element: <Navigate to={`/${RouteConstants.LandingPage}`} />,
          index: true,
        },
      ],
    },
  ],
  {
    future: {},
  },
);
